import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import client, { inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Package, ShoppingCart, Calendar, Briefcase, LogOut, Boxes } from "lucide-react";
import OrderDetailModal, { ShippingBadge } from "@/components/OrderDetailModal";

export default function Admin() {
  const { user, checked, logout } = useAuth();
  const [tab, setTab] = useState("products");

  if (checked && (!user || user.role !== "admin")) return <Navigate to="/login" replace />;
  if (!user) return <div className="pt-40 text-center">Loading…</div>;

  return (
    <div data-testid="admin-page" className="pt-28 pb-24 max-w-[1600px] mx-auto px-6 md:px-12">
      <div className="flex justify-between items-end mb-12">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">
            Admin Dashboard
          </div>
          <h1 className="font-serif-display text-5xl font-light leading-none">
            Estate control room.
          </h1>
        </div>
        <button
          onClick={logout}
          className="border border-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em] inline-flex items-center gap-2 transition-colors"
          data-testid="admin-logout"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="border-b border-[#EBE3D5] flex gap-10 mb-12">
        {[
          { id: "products", label: "Products", Icon: Package },
          { id: "orders", label: "Orders", Icon: ShoppingCart },
          { id: "inventory", label: "Inventory", Icon: Boxes },
          { id: "estates", label: "Estates", Icon: Mountain },
          { id: "bookings", label: "Bookings", Icon: Calendar },
          { id: "enquiries", label: "B2B Enquiries", Icon: Briefcase },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`pb-4 flex items-center gap-2 text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-[#C85A32] text-[#C85A32]" : "border-transparent text-[#7B6F66]"
            }`}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductsTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "inventory" && <InventoryTab />}
      {tab === "estates" && <EstatesTab />}
      {tab === "bookings" && <BookingsTab />}
      {tab === "enquiries" && <EnquiriesTab />}
    </div>
  );
}

function ProductsTab() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = () => client.get("/products").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    try {
      await client.delete(`/products/${slug}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-[#7B6F66]">{items.length} products</div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="admin-new-product"
          className="bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em] transition-colors"
        >
          + New Product
        </button>
      </div>
      <div className="border border-[#EBE3D5]">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#EBE3D5] text-[10px] uppercase tracking-[0.2em] text-[#7B6F66]">
          <div className="col-span-5">Product</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Price</div>
          <div className="col-span-2">Featured</div>
          <div className="col-span-1"></div>
        </div>
        {items.map((p) => (
          <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#EBE3D5] items-center" data-testid={`admin-product-row-${p.slug}`}>
            <div className="col-span-5 flex items-center gap-3">
              <img src={p.image} alt="" className="w-12 h-14 object-cover bg-[#F4EFE6]" />
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-[#7B6F66]">{p.slug}</div>
              </div>
            </div>
            <div className="col-span-2 text-sm">{p.category}</div>
            <div className="col-span-2 text-sm">{inr(p.variants?.[0]?.price || p.price)}</div>
            <div className="col-span-2 text-sm">{p.featured ? "Yes" : "—"}</div>
            <div className="col-span-1 text-right">
              <button
                onClick={() => remove(p.slug)}
                className="text-xs text-[#C85A32] hover:underline"
                data-testid={`admin-delete-${p.slug}`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      {showNew && <NewProductModal onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}

function NewProductModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    slug: "",
    name: "",
    category: "arabica",
    tagline: "",
    description: "",
    price: 500,
    image: "",
    featured: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/products", {
        ...form,
        price: parseFloat(form.price),
        variants: [{ label: "250g", price: parseFloat(form.price), stock: 100 }],
      });
      toast.success("Product created");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#2C1E16]/70 backdrop-blur-sm flex items-center justify-center px-6" onClick={onClose}>
      <div className="bg-[#FDFBF7] max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif-display text-3xl mb-6">New Product</h3>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Slug (e.g. new-blend)" value={form.slug} onChange={set("slug")} data-testid="new-product-slug" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <input required placeholder="Name" value={form.name} onChange={set("name")} data-testid="new-product-name" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <select value={form.category} onChange={set("category")} data-testid="new-product-category" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent">
            <option value="arabica">Arabica</option>
            <option value="robusta">Robusta</option>
            <option value="single-origin">Single Origin</option>
            <option value="blends">Blends</option>
            <option value="decaf">Decaf</option>
            <option value="equipment">Equipment</option>
          </select>
          <input placeholder="Tagline" value={form.tagline} onChange={set("tagline")} className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <textarea rows={3} placeholder="Description" value={form.description} onChange={set("description")} className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <input required type="number" placeholder="Price (INR)" value={form.price} onChange={set("price")} data-testid="new-product-price" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <input required placeholder="Image URL" value={form.image} onChange={set("image")} data-testid="new-product-image" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={set("featured")} /> Featured on home page
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#1C3326] px-6 py-3 uppercase text-xs tracking-[0.3em]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="new-product-submit" className="flex-1 bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.3em] disabled:opacity-60">
              {busy ? "Saving…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = () => client.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await client.patch(`/orders/${id}/status?status=${status}`);
      toast.success(`Order marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (orders.length === 0) {
    return <div className="border border-[#EBE3D5] p-16 text-center font-serif-display text-3xl text-[#7B6F66]">No orders yet.</div>;
  }

  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} className="border border-[#EBE3D5] p-6" data-testid={`admin-order-${o.order_number}`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-serif-display text-2xl">{o.order_number}</div>
                <ShippingBadge status={o.shipping_status} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#7B6F66]">
                  {o.payment_method?.toUpperCase()} · {o.payment_status?.toUpperCase()}
                </span>
              </div>
              <div className="text-sm text-[#7B6F66]">{o.email} · {new Date(o.created_at).toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={o.status}
                onChange={(e) => updateStatus(o.id, e.target.value)}
                data-testid={`admin-order-status-${o.order_number}`}
                className="border border-[#EBE3D5] px-3 py-2 text-xs uppercase tracking-[0.2em] bg-transparent"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={() => setOpenId(o.id)}
                data-testid={`admin-order-view-${o.order_number}`}
                className="bg-[#1C3326] hover:bg-[#15261C] text-[#FDFBF7] px-4 py-2 uppercase text-xs tracking-[0.2em]"
              >
                Manage
              </button>
              <div className="font-serif-display text-2xl">{inr(o.total)}</div>
            </div>
          </div>
        </div>
      ))}
      {openId && <OrderDetailModal orderId={openId} onClose={() => setOpenId(null)} onChange={load} />}
    </div>
  );
}

function InventoryTab() {
  const [data, setData] = useState({ threshold: 10, rows: [] });
  const [shipCfg, setShipCfg] = useState(null);
  const [editing, setEditing] = useState(null); // {slug, variant_label, stock}
  const [busy, setBusy] = useState(false);

  const load = () => {
    client.get("/inventory").then((r) => setData(r.data));
    client.get("/shipping/status").then((r) => setShipCfg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await client.patch(`/products/${editing.slug}/stock`, {
        variant_label: editing.variant_label,
        stock: parseInt(editing.stock),
      });
      toast.success("Stock updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const low = data.rows.filter((r) => r.status !== "ok").length;
  const out = data.rows.filter((r) => r.status === "out").length;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="border border-[#EBE3D5] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#7B6F66] mb-1">SKUs</div>
          <div className="font-serif-display text-4xl text-[#1C3326]">{data.rows.length}</div>
        </div>
        <div className="border border-[#EBE3D5] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#7B6F66] mb-1">Low stock (≤ {data.threshold})</div>
          <div className="font-serif-display text-4xl text-[#C85A32]">{low}</div>
        </div>
        <div className="border border-[#EBE3D5] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#7B6F66] mb-1">Out of stock</div>
          <div className="font-serif-display text-4xl text-red-700">{out}</div>
        </div>
        <div className="border border-[#EBE3D5] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#7B6F66] mb-1">Shiprocket</div>
          <div className="font-serif-display text-xl text-[#1C3326]">
            {shipCfg?.configured ? "Connected" : "Not connected"}
          </div>
          {shipCfg && (
            <div className="text-xs text-[#7B6F66] mt-1">Pickup: {shipCfg.pickup_location || "—"}</div>
          )}
        </div>
      </div>

      <div className="border border-[#EBE3D5]">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#EBE3D5] text-[10px] uppercase tracking-[0.2em] text-[#7B6F66]">
          <div className="col-span-5">Product</div>
          <div className="col-span-2">Variant</div>
          <div className="col-span-2">Price</div>
          <div className="col-span-2">Stock</div>
          <div className="col-span-1"></div>
        </div>
        {data.rows.map((r) => (
          <div
            key={`${r.slug}-${r.variant_label}`}
            data-testid={`inventory-row-${r.slug}-${r.variant_label}`}
            className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#EBE3D5] items-center text-sm"
          >
            <div className="col-span-5 flex items-center gap-3">
              <img src={r.image} alt="" className="w-10 h-12 object-cover bg-[#F4EFE6]" />
              <div>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-[#7B6F66]">{r.category}</div>
              </div>
            </div>
            <div className="col-span-2">{r.variant_label || "—"}</div>
            <div className="col-span-2">{inr(r.price)}</div>
            <div className="col-span-2">
              <span
                className={`px-2 py-0.5 text-xs ${
                  r.status === "out"
                    ? "bg-red-100 text-red-800"
                    : r.status === "low"
                    ? "bg-[#F4EFE6] text-[#C85A32] border border-[#C85A32]"
                    : "text-[#1C3326]"
                }`}
              >
                {r.stock}
              </span>
            </div>
            <div className="col-span-1 text-right">
              <button
                onClick={() => setEditing({ slug: r.slug, variant_label: r.variant_label, stock: r.stock })}
                data-testid={`inventory-edit-${r.slug}-${r.variant_label}`}
                className="text-xs uppercase tracking-[0.2em] text-[#C85A32] link-underline"
              >
                Adjust
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-[#2C1E16]/70 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setEditing(null)}>
          <div className="bg-[#FDFBF7] max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">Adjust stock</div>
            <h3 className="font-serif-display text-2xl mb-6">
              {editing.slug} · {editing.variant_label}
            </h3>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.15em] text-[#7B6F66] mb-2">Units on hand</div>
              <input
                type="number"
                min={0}
                value={editing.stock}
                onChange={(e) => setEditing({ ...editing, stock: e.target.value })}
                data-testid="stock-input"
                className="w-full bg-transparent border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none"
              />
            </label>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 border border-[#1C3326] px-6 py-3 uppercase text-xs tracking-[0.3em]">Cancel</button>
              <button
                onClick={save}
                disabled={busy}
                data-testid="stock-save"
                className="flex-1 bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.3em] disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EstatesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => client.get("/estates?all=true").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (slug, visible) => {
    try {
      await client.patch(`/estates/${slug}/visibility?visible=${visible}`);
      toast.success(`Estate ${visible ? "shown" : "hidden"} on site`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete estate ${slug}?`)) return;
    try {
      await client.delete(`/estates/${slug}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-[#7B6F66]">
          {items.length} estates · {items.filter((e) => e.visible).length} shown on site
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="admin-new-estate"
          className="bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.25em] transition-colors"
        >
          + New Estate
        </button>
      </div>
      <div className="space-y-3">
        {items.map((e) => (
          <div key={e.id} className="border border-[#EBE3D5] p-5" data-testid={`admin-estate-${e.slug}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex gap-4 flex-1">
                {e.image && <img src={e.image} alt="" className="w-24 h-24 object-cover bg-[#F4EFE6]" />}
                <div className="flex-1">
                  <div className="font-serif-display text-2xl text-[#1C3326]">{e.name}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#7B6F66] mt-1">{e.region}</div>
                  {e.tagline && <p className="text-sm text-[#4A3B32] mt-2">{e.tagline}</p>}
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-3 cursor-pointer" data-testid={`estate-toggle-${e.slug}`}>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#7B6F66]">
                    {e.visible ? "Visible" : "Hidden"}
                  </span>
                  <span
                    onClick={() => toggle(e.slug, !e.visible)}
                    className={`relative w-11 h-6 border transition-colors ${
                      e.visible ? "bg-[#1C3326] border-[#1C3326]" : "bg-transparent border-[#EBE3D5]"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[#FDFBF7] border border-[#EBE3D5] transition-transform ${
                        e.visible ? "translate-x-5" : ""
                      }`}
                    />
                  </span>
                </label>
                <button
                  onClick={() => setEditing(e)}
                  data-testid={`estate-edit-${e.slug}`}
                  className="text-xs uppercase tracking-[0.2em] text-[#1C3326] link-underline"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(e.slug)}
                  data-testid={`estate-delete-${e.slug}`}
                  className="text-xs uppercase tracking-[0.2em] text-[#C85A32] link-underline"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {(showNew || editing) && (
        <EstateEditModal
          estate={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EstateEditModal({ estate, onClose, onSaved }) {
  const [form, setForm] = useState(estate || {
    slug: "", name: "", region: "", altitude: "", established: "",
    tagline: "", story: "", image: "", varieties: [], visible: true, order: 100,
  });
  const [varietiesText, setVarietiesText] = useState(
    (estate?.varieties || []).join(", ")
  );
  const [busy, setBusy] = useState(false);
  const isEdit = !!estate;

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      varieties: varietiesText.split(",").map((s) => s.trim()).filter(Boolean),
      order: parseInt(form.order) || 100,
    };
    try {
      if (isEdit) await client.put(`/estates/${estate.slug}`, payload);
      else await client.post("/estates", payload);
      toast.success(isEdit ? "Estate updated" : "Estate created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#2C1E16]/70 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FDFBF7] max-w-2xl w-full p-8 my-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif-display text-3xl mb-6">{isEdit ? "Edit estate" : "New estate"}</h3>
        <form onSubmit={submit} className="space-y-3">
          {!isEdit && (
            <input required placeholder="Slug (e.g. sakleshpur)" value={form.slug} onChange={set("slug")} data-testid="estate-slug" className={inp} />
          )}
          <input required placeholder="Estate name" value={form.name} onChange={set("name")} data-testid="estate-name" className={inp} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Region (e.g. Sakleshpur, Karnataka)" value={form.region} onChange={set("region")} className={inp} />
            <input placeholder="Altitude (e.g. 1,050–1,150 m)" value={form.altitude} onChange={set("altitude")} className={inp} />
          </div>
          <input placeholder="Established / heritage tagline" value={form.established} onChange={set("established")} className={inp} />
          <input placeholder="One-line tagline" value={form.tagline} onChange={set("tagline")} className={inp} />
          <textarea placeholder="The story" rows={6} value={form.story} onChange={set("story")} data-testid="estate-story" className={inp} />
          <input placeholder="Image URL" value={form.image} onChange={set("image")} className={inp} />
          <input placeholder="Varieties (comma-separated, e.g. Arabica S795, Robusta CxR)" value={varietiesText} onChange={(e) => setVarietiesText(e.target.value)} className={inp} />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Display order" value={form.order} onChange={set("order")} className={inp} />
            <label className="flex items-center gap-2 border border-[#EBE3D5] px-4">
              <input type="checkbox" checked={form.visible} onChange={set("visible")} />
              <span className="text-sm">Visible on site</span>
            </label>
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-[#1C3326] px-6 py-3 uppercase text-xs tracking-[0.3em]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="estate-save" className="flex-1 bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] px-6 py-3 uppercase text-xs tracking-[0.3em] disabled:opacity-60">
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create estate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent";

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    client.get("/bookings").then((r) => setBookings(r.data));
  }, []);
  if (bookings.length === 0) {
    return <div className="border border-[#EBE3D5] p-16 text-center font-serif-display text-3xl text-[#7B6F66]">No bookings yet.</div>;
  }
  return (
    <div className="border border-[#EBE3D5]">
      <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#EBE3D5] text-[10px] uppercase tracking-[0.2em] text-[#7B6F66]">
        <div className="col-span-3">Service</div>
        <div className="col-span-3">Guest</div>
        <div className="col-span-2">Date</div>
        <div className="col-span-1">Guests</div>
        <div className="col-span-3">Contact</div>
      </div>
      {bookings.map((b) => (
        <div key={b.id} className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#EBE3D5] text-sm" data-testid={`admin-booking-${b.id}`}>
          <div className="col-span-3 capitalize">{b.service.replace(/-/g, " ")}</div>
          <div className="col-span-3">{b.name}</div>
          <div className="col-span-2">{b.date}</div>
          <div className="col-span-1">{b.guests}</div>
          <div className="col-span-3 text-xs text-[#7B6F66]">{b.email}<br />{b.phone}</div>
        </div>
      ))}
    </div>
  );
}

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    client.get("/business-enquiries").then((r) => setItems(r.data));
  }, []);
  if (items.length === 0) {
    return <div className="border border-[#EBE3D5] p-16 text-center font-serif-display text-3xl text-[#7B6F66]">No B2B enquiries yet.</div>;
  }
  return (
    <div className="space-y-4">
      {items.map((e) => (
        <div key={e.id} className="border border-[#EBE3D5] p-6" data-testid={`admin-enquiry-${e.id}`}>
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div>
              <div className="font-serif-display text-2xl text-[#1C3326]">{e.company}</div>
              <div className="text-sm text-[#7B6F66]">
                {e.contact_name} · {e.business_type}{e.city && ` · ${e.city}`}
              </div>
              <div className="text-sm mt-1">
                <a href={`mailto:${e.email}`} className="link-underline">{e.email}</a>
                {" · "}
                <a href={`tel:${e.phone}`} className="link-underline">{e.phone}</a>
              </div>
            </div>
            <div className="text-xs text-[#7B6F66] whitespace-nowrap">
              {new Date(e.created_at).toLocaleString()}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#EBE3D5] text-sm text-[#4A3B32] grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#7B6F66] mb-1">Volume</div>
              {e.monthly_volume || "—"}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#7B6F66] mb-1">Interested in</div>
              {e.interests?.length ? e.interests.join(", ") : "—"}
            </div>
            {e.message && (
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#7B6F66] mb-1">Message</div>
                {e.message}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
